import { createUseStyles } from "react-jss";
import { v4 as uuid } from "uuid";
import produce from "immer";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faTrash } from "@fortawesome/pro-light-svg-icons";

import TopBar from "../components/refresh/TopBar";
import ModeSelect from "../components/ModeSelect";
import Button from "../components/refresh/Button";
import InlineEdit from "../components/InlineEdit";
import Checkbox from "../components/refresh/inputs/Checkbox";
import { Colors } from "../config";
import Lightbox from "../components/Lightbox";
import AutoplayImageSelector from "../components/refresh/selectors/AutoplayImageSelector";
import ImageSelectorModal from "../components/refresh/selectors/ImageSelectorModal";

const flowsFromTour = tour => {
  return tour?.data?.flows ?? [];
};

const imagesFromTour = tour => {
  return tour?.data?.images ?? [];
};

// Flows referencing image IDs that no longer exist in the tour,
// with how many references are broken in each.
const staleFlowsFromTour = tour => {
  const images = tour?.data?.images ?? {};
  return flowsFromTour(tour)
    .map(flow => {
      let missing = 0;
      if (flow.start && !images[flow.start]) missing++;
      for (const step of flow.steps ?? []) {
        if (step?.image && !images[step.image]) missing++;
      }
      return { id: flow.id, name: flow.name, missing };
    })
    .filter(flow => flow.missing > 0);
};

const Autoplay = ({
  onEmbedTour,
  onSaveTour,
  onExit,
  dirty,
  tour,
  onUpdateTour,
  onTourSettings,
}) => {
  const classes = useStyles();
  const flows = flowsFromTour(tour);
  const [activeFlow, setActiveFlow] = useState(null);
  const [staleFlows, setStaleFlows] = useState([]);

  useEffect(() => {
    setStaleFlows(staleFlowsFromTour(tour));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeDeletedImages = () => {
    onUpdateTour(
      produce(tour, draft => {
        const images = draft.data.images ?? {};
        draft.data.flows = (draft.data.flows ?? []).map(flow => {
          let steps = (flow.steps ?? []).filter(
            step => !step?.image || images[step.image],
          );
          let start = flow.start;
          if (start && !images[start]) {
            start = steps[0]?.image ?? null;
            steps = steps.slice(1);
          }
          return { ...flow, start, steps };
        });
      }),
    );
    setStaleFlows([]);
  };

  const saveFlow = flow => {
    onUpdateTour(
      produce(tour, draft => {
        draft.data.flows ??= [];
        const index = draft.data.flows.findIndex(f => f.id === flow.id);
        if (index >= 0) {
          draft.data.flows[index] = flow;
        } else {
          draft.data.flows.push(flow);
        }
      }),
    );
  };

  const onCheckStartingFlow = flowId => {
    onUpdateTour(
      produce(tour, draft => {
        draft.data.options.startingFlow = flowId;
      }),
    );
  };

  const deleteActiveFlow = () => {
    if (!window.confirm("Are you sure you want to delete this Autoplay route?"))
      return;
    const id = activeFlow;
    setActiveFlow(null);
    onUpdateTour(
      produce(tour, draft => {
        draft.data.flows ??= [];
        draft.data.flows = draft.data.flows.filter(f => f.id !== id);
      }),
    );
  };

  return (
    <div className={classes.container}>
      <TopBar
        links={{ onTourSettings, onEmbedTour, onSaveTour, dirty, onExit }}
      />
      <div className={classes.mainContainer}>
        {activeFlow ? (
          <FlowEditor
            flow={flows.find(x => x.id === activeFlow)}
            onChange={saveFlow}
            onBack={() => setActiveFlow(null)}
            onDelete={deleteActiveFlow}
            images={imagesFromTour(tour)}
            startingFlow={tour.data?.options?.startingFlow}
            onCheckStartingFlow={onCheckStartingFlow}
          />
        ) : (
          <div className={classes.flowSelect}>
            <div className={classes.flowSelectHeader}>
              <div className={classes.flowSelectHeaderTitle}>
                <h1>Autoplay Routes</h1>
                <p>Create and manage autoplay routes</p>
              </div>
              <Button
                onClick={() =>
                  saveFlow({
                    id: uuid(),
                    name: "New Flow",
                    steps: [],
                    start: null,
                  })
                }
              >
                New Flow
              </Button>
            </div>
            <hr />
            <div className={classes.flowSelectFlows}>
              {flows.map(flow => (
                <div
                  key={flow.id}
                  className={classes.flowCard}
                  onClick={() => setActiveFlow(flow.id)}
                >
                  <div className={classes.flowCardLeft}>
                    <h2>{flow.name}</h2>
                    <p>
                      {(() => {
                        const stepCount = (flow.steps?.length || 0) + (flow.start ? 1 : 0);
                        return `${stepCount} step${stepCount !== 1 ? "s" : ""}`;
                      })()}
                    </p>
                    {tour.data?.options?.startingFlow === flow.id && (
                      <span className={classes.startingFlowTitle}>
                        Starting Flow
                      </span>
                    )}
                  </div>
                  <div className={classes.flowCardRight}>
                    {flow.start && tour.data?.images?.[flow.start] && (
                      <img
                        src={
                          tour.data.images[flow.start].urls.full +
                          "&w=60&h=60&auto=format&fit=crop"
                        }
                        alt=""
                      />
                    )}
                    {(flow.steps ?? []).slice(0, 4).map(step => step?.image && tour.data?.images?.[step.image] && (
                      <img
                        key={step.id}
                        src={
                          tour.data.images[step.image].urls.full +
                          "&w=60&h=60&auto=format&fit=crop"
                        }
                        alt=""
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Lightbox
        show={staleFlows.length > 0}
        onClose={() => setStaleFlows([])}
        className={classes.staleModal}
      >
        <h3>Deleted Images in Autoplay</h3>
        <p>
          Some Autoplay routes use images that have been deleted from this
          tour:
        </p>
        <ul>
          {staleFlows.map(flow => (
            <li key={flow.id}>
              <strong>{flow.name}</strong> — {flow.missing} missing image
              {flow.missing !== 1 ? "s" : ""}
            </li>
          ))}
        </ul>
        <p>Do you want to remove these images from the Autoplay routes?</p>
        <div className={classes.staleModalButtons}>
          <Button onClick={() => setStaleFlows([])}>Keep</Button>
          <Button onClick={removeDeletedImages}>Remove Missing Images</Button>
        </div>
      </Lightbox>
      <ModeSelect />
    </div>
  );
};

const getNextImagesOfId = (images, id) => {
  if (!images[id]?.hotspots) return [];

  return images[id].hotspots
    .filter(hotspot => ["link", "elevate-up", "elevate-down", "fade"].includes(hotspot.type))
    .map(hotspot => images[hotspot.options.id]);
}

const FlowEditor = ({ flow, onChange, onBack, onDelete, images, startingFlow, onCheckStartingFlow }) => {
  const classes = useStyles();
  const imageSelectorRef = useRef(null);
  const selectedStep = useRef(null);
  console.log("flowflow", flow)

  const onDeleteImage = useCallback((index) => {
    let steps = [...flow.steps];
    let startImg = flow.start;
    if (index === 0) {
      startImg = steps[0]?.image || null;
      steps = steps.slice(1);
    } else {
      steps.splice(index - 1, 1);
    }
    onChange({ ...flow, steps, start: startImg });
  }, [flow, onChange])

  const onSelectStep = (index) => {
    selectedStep.current = index;
    if (index === 0) {
      imageSelectorRef?.current?.show();
    } else {
      const prevId = index === 1 ? flow.start : flow?.steps?.[index - 2]?.image;
      if (!prevId) {
        return;
      }
      const filteredImages = getNextImagesOfId(images, prevId);
      imageSelectorRef?.current?.show(filteredImages);
    }
  }

  const onSelectImage = (image) => {
    if (selectedStep.current === 0) {
      onChange({ ...flow, start: image.id });
    } else {
      const steps = [...flow.steps];
      const currentStep = selectedStep.current - 1;
      steps[currentStep] = { ...steps[currentStep], image: image.id };
      onChange({ ...flow, steps });
    }
  }

  const validatedSteps = useMemo(() => {
    if (!flow.steps) return [];
    return flow.steps.map((step, idx) => {
      if (!step) return { id: uuid(), type: "image", image: null, isValid: false };
      const nextImgsOfPrev = getNextImagesOfId(images, idx === 0 ? flow.start : flow.steps?.[idx - 1]?.image);
      return { ...step, isValid: step.image ? nextImgsOfPrev.some(img => img.id === step.image) : false };
    });
  }, [flow, images]);

  return (
    <div className={classes.flowEditor}>
      <div className={classes.flowEditorHeader}>
        <FontAwesomeIcon
          icon={faChevronLeft}
          className={classes.flowEditorHeaderIcon}
          onClick={onBack}
        />
        <InlineEdit
          tagName="span"
          className={classes.flowEditorHeaderTitle}
          value={flow.name}
          onChange={name => onChange({ ...flow, name })}
        />
        <div className={classes.grow} />
        <Checkbox
          checked={startingFlow === flow.id}
          margin="5px 0 0"
          onChange={s => onCheckStartingFlow(s ? flow.id : null)}
          className={classes.startingFlowCheckbox}
        >
          Starting Flow
        </Checkbox>
        <FontAwesomeIcon
          icon={faTrash}
          className={classes.flowEditorHeaderIcon}
          onClick={onDelete}
        />
      </div>
      <hr />
      <div className={classes.flowEditorContent}>
        <div className={classes.flowEditorSteps}>
          <div className={classes.flowEditorStep}>
            <div className={classes.flowEditorStepHeader}>
              <span>1</span>
              {/* <FontAwesomeIcon
                icon={faTrash}
                className={classes.flowEditorHeaderIcon}
                onClick={() => onDeleteImage(0)}
              /> */}
            </div>
            <AutoplayImageSelector
              image={images?.[flow.start]}
              className={classes.imageSelector}
              onClick={() => onSelectStep(0)}
            />
          </div>
          <div className={classes.flowEditorLine} />
          {validatedSteps.map((step, i) => {
            return (
              <Fragment key={step.id}>
                <div className={classes.flowEditorStep}>
                  <div className={classes.flowEditorStepHeader}>
                    <span>{i + 2}</span>
                    <FontAwesomeIcon
                      icon={faTrash}
                      className={classes.imageDeleteButton}
                      onClick={() => onDeleteImage(i + 1)}
                    />
                  </div>
                  <AutoplayImageSelector
                    image={images?.[step.image]}
                    className={classes.imageSelector}
                    onClick={() => onSelectStep(i + 1)}
                    errorMessage={!step.isValid && step.image ? "This image is not connected to the previous step" : undefined}
                  />
                </div>
                <div className={classes.flowEditorLine} />
              </Fragment>
            );
          })}
          <Button
            className={classes.flowEditorStepAdd}
            disabled={
              !flow.start ||
              (flow.steps.length > 0 &&
                !flow.steps[flow.steps.length - 1].image)
            }
            onClick={() =>
              onChange({
                ...flow,
                steps: [
                  ...flow.steps,
                  { id: uuid(), type: "image", image: null },
                ],
              })
            }
          >
            + Add Step
          </Button>
        </div>
      </div>
      <ImageSelectorModal
        ref={imageSelectorRef}
        images={images}
        onSubmit={onSelectImage}
      />
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  mainContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: "#333",
    color: "#fff",
    height: "100%",
  },
  grow: { flex: 1 },
  flowSelect: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    width: "95%",
    margin: "50px auto 0",
    maxWidth: 1000,
    backgroundColor: "#3f3f3f",
    borderRadius: [15, 15, 0, 0],
    "& hr": {
      width: "100%",
      boxSizing: "border-box",
      margin: 0,
    },
  },
  flowSelectHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: [0, 15],
    height: 80,
  },
  flowSelectHeaderTitle: {
    "& h1": {
      fontSize: "1.5em",
      margin: 0,
    },
    "& p": {
      fontSize: "1em",
      margin: 0,
      opacity: 0.7,
    },
  },
  flowSelectFlows: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflow: "auto",
    padding: [15, 15, 0],
    maxHeight: "calc(100vh - 234px)",
  },
  flowCard: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 15,
    cursor: "pointer",
    transition: "background-color 0.2s ease-in-out",
    backgroundColor: "#444",
    marginBottom: 10,
    borderRadius: 5,
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  flowCardLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    "& h2": {
      fontSize: "1.3em",
      margin: 0,
    },
    "& p": {
      fontSize: "1em",
      opacity: 0.7,
      margin: 0,
    },
  },
  flowCardRight: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    "& img": {
      marginLeft: 15,
      borderRadius: 5,
    }
  },
  flowEditor: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    height: "100%",
    "& hr": {
      width: "100%",
      boxSizing: "border-box",
      margin: 0,
    },
  },
  flowEditorHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: 50,
    padding: [0, 10],
  },
  flowEditorHeaderIcon: {
    height: 15,
    width: 15,
    padding: 10,
    cursor: "pointer",
    transition: "background-color 0.2s",
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "#555",
    },
  },
  imageDeleteButton: {
    height: 14,
    width: 14,
    padding: 6,
    cursor: "pointer",
    transition: "background-color 0.2s",
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "#555",
    },
  },
  flowEditorHeaderTitle: {
    fontSize: "1.5em",
    margin: [0, 0, 0, 10],
  },
  flowEditorContent: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflow: "auto",
  },

  flowEditorSteps: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    margin: [0, "auto"],
    maxWidth: 1000,
    width: "calc(100% - 30%)",
    padding: [15, 0],
  },
  flowEditorStepHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: [0, 5],
    height: 34,
    "& span": {
      border: "1px solid #fff",
      borderRadius: "50%",
      width: 20,
      height: 20,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  },
  flowEditorLine: {
    width: 2,
    height: 40,
    backgroundColor: "#fff",
    margin: [0, "auto", 0, 40],
    flexShrink: 0,
  },
  flowEditorStep: {
    borderRadius: 10,
    backgroundColor: "#444",
  },
  imageSelector: {
    marginTop: 0,
  },
  flowEditorStepAdd: {
    width: 125,
    height: 40,
    "&:hover": {
      backgroundColor: "#444",
    },
  },
  startingFlowTitle: {
    color: Colors.Orange,
  },
  startingFlowCheckbox: {
    marginRight: 12,
  },
  staleModal: {
    maxWidth: 420,
    alignItems: "flex-start !important",
    "& p": {
      margin: [0, 0, 10],
    },
    "& ul": {
      margin: [0, 0, 10],
      paddingLeft: 20,
    },
    "& li": {
      marginBottom: 4,
    },
  },
  staleModalButtons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    width: "100%",
    marginTop: 10,
  },
});

export default Autoplay;
